/**
 * @fileoverview Reusable icon component using individual React Icons imports.
 * Migrated to Tailwind CSS.
 */

import clsx from 'clsx';
import {
  BsBookHalf,
  BsJournalCheck,
  BsLightbulb,
  BsArrowLeft,
  BsArrowRight,
  BsGearFill,
  BsSliders,
  BsPaletteFill,
  BsSunFill,
  BsMoonStarsFill,
  BsBookFill,
  BsFonts,
  BsType,
  BsTypeH1,
  BsXCircle,
  BsXLg,
  BsChevronLeft,
  BsChevronRight,
  BsGeoAltFill,
  BsExclamationTriangleFill,
  BsArrowClockwise,
  BsHouseFill,
  BsSearch,
  BsHeartFill,
  BsBookmark,
  BsBookmarkFill,
} from 'react-icons/bs';

type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface IconProps {
  name: string;
  size?: IconSize;
  className?: string;
  'aria-label'?: string;
  'aria-hidden'?: boolean;
  id?: string;
  'data-testid'?: string;
}

// Map icon names to components
const iconMap: Record<string, React.ComponentType> = {
  'book-half': BsBookHalf,
  'journal-check': BsJournalCheck,
  lightbulb: BsLightbulb,
  'arrow-left': BsArrowLeft,
  'arrow-right': BsArrowRight,
  'gear-fill': BsGearFill,
  sliders: BsSliders,
  'palette-fill': BsPaletteFill,
  'sun-fill': BsSunFill,
  'moon-stars-fill': BsMoonStarsFill,
  'book-fill': BsBookFill,
  fonts: BsFonts,
  type: BsType,
  'type-h1': BsTypeH1,
  'x-circle': BsXCircle,
  'x-lg': BsXLg,
  'chevron-left': BsChevronLeft,
  'chevron-right': BsChevronRight,
  'geo-alt-fill': BsGeoAltFill,
  'exclamation-triangle-fill': BsExclamationTriangleFill,
  'arrow-clockwise': BsArrowClockwise,
  'house-fill': BsHouseFill,
  search: BsSearch,
  'heart-fill': BsHeartFill,
  bookmark: BsBookmark,
  'bookmark-fill': BsBookmarkFill,
};

// Size classes using Tailwind
const sizeClasses: Record<IconSize, string> = {
  xs: 'text-xs',
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-2xl',
  xl: 'text-3xl',
};

/**
 * Icon component using React Icons (Bootstrap Icons set).
 */
export function Icon({
  name,
  size = 'md',
  className,
  'aria-label': ariaLabel,
  'aria-hidden': ariaHidden = !ariaLabel,
  ...props
}: IconProps) {
  const IconComponent = iconMap[name];

  if (!IconComponent) {
    console.warn(`Icon "${name}" not found. Add it to the iconMap and import it.`);
    return null;
  }

  return (
    <IconComponent
      {...({
        className: clsx('inline-block leading-none align-middle', sizeClasses[size], className),
        'aria-label': ariaLabel,
        'aria-hidden': ariaHidden,
        role: ariaLabel ? 'img' : undefined,
        ...props,
      } as any)}
    />
  );
}
